import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { adminSeasonsService } from '../services/admin.seasons.service'
import { adminFilmsService } from '../services/admin.films.service'
import { adminCompanyService } from '../services/admin.company.service'

interface DashboardCounts {
  seasons: number
  films: number
  members: number
}

export function AdminDashboardPage() {
  const [counts, setCounts] = useState<DashboardCounts | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      adminSeasonsService.getAllSeasons(),
      adminFilmsService.getAllFilms(),
      adminCompanyService.getAllMembers(),
    ])
      .then(([seasons, films, members]) => {
        setCounts({
          seasons: seasons.length,
          films: films.length,
          members: members.length,
        })
      })
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load dashboard data'))
      )
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Manage your One Day Dance content.</p>
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Seasons
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-9 w-16" />
            ) : (
              <p className="text-4xl font-bold">{counts?.seasons ?? 0}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Films
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-9 w-16" />
            ) : (
              <p className="text-4xl font-bold">{counts?.films ?? 0}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Company Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-9 w-16" />
            ) : (
              <p className="text-4xl font-bold">{counts?.members ?? 0}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to="/admin/seasons">Manage Seasons</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/films/new">Add Film</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/company">Manage Company</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
